import { useEffect, useRef, useState } from "react";
import { ChevronDown, Plus, Search, X } from "lucide-react";
import clsx from "clsx";

import { IEMRSectionSearchResponseData } from "@/api_framework/api_modals/EMRs";
import { SectionSearchDataTypes, useEMRSectionSearchAPI } from "@/app/hooks/api_hooks/emr/useEMRSectionSearchAPI";

// Option lists mirror the backend enums the voice agent already emits
// (MedicineUnits / MedicineFrequency / MedicineTiming). Editing must not be able
// to produce a value the EMR would reject.
export const MEDICINE_UNITS = [
  "Tablet", "Capsule", "ml", "gm", "Drops", "Sachet", "Cream", "Gel", "Chews",
  "Lotion", "Pipette", "Shampoo", "Spray", "Ointment", "Powder",
];
export const MEDICINE_FREQUENCIES = ["Once weekly", "Twice weekly", "Every X weeks"];
export const MEDICINE_TIMINGS = ["Before food", "After food", "With food", "Empty stomach", "As needed"];

/** Which existing EMR fields each section allows editing. */
export type ItemField = "quantity" | "dose" | "duration_unit" | "frequency" | "x_value" | "duration" | "timing" | "remark";

type NamedItem = { name?: string };

const read = (item: NamedItem, field: string): unknown => (item as Record<string, unknown>)[field];

const FIELD_LABEL: Record<ItemField, string> = {
  quantity: "Quantity",
  dose: "Dose",
  duration_unit: "Unit",
  frequency: "Frequency",
  x_value: "Every X",
  duration: "Duration (days)",
  timing: "Timing",
  remark: "Remark",
};

const NUMERIC: ItemField[] = ["quantity", "dose", "duration", "x_value"];
const OPTIONS: Partial<Record<ItemField, string[]>> = {
  duration_unit: MEDICINE_UNITS,
  frequency: MEDICINE_FREQUENCIES,
  timing: MEDICINE_TIMINGS,
};

/**
 * One input skin for the whole voice panel. Every control — textarea, date field,
 * dose box, catalog search — shares the border, radius and focus ring from here,
 * so the parsed EMR reads as one form instead of a stack of differently-styled
 * boxes. Sizing (padding / text size) is added per use: the section fields are
 * roomy, the item-editor grid is dense.
 */
export const EMR_INPUT_BASE =
  "rounded-lg border border-slate-200 bg-white text-slate-800 placeholder-slate-400 transition-colors "
  + "hover:border-slate-300 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20";

/** Roomy variant — the labelled fields inside each EMR section. */
export const EMR_INPUT_CLASS = `w-full ${EMR_INPUT_BASE} px-3 py-2 text-sm`;

/** Dense variant — the two-column grid inside an expanded item row. */
const inputClass = `w-full ${EMR_INPUT_BASE} px-2 py-1.5 text-[11px]`;

const ItemFieldInput = ({
  field,
  value,
  onChange,
}: {
  field: ItemField;
  value: unknown;
  onChange: (next: unknown) => void;
}) => {
  const options = OPTIONS[field];

  if (options) {
    return (
      <select
        className={inputClass}
        value={(value as string) ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (NUMERIC.includes(field)) {
    return (
      <input
        type="number"
        min={0}
        className={inputClass}
        value={value === null || value === undefined ? "" : String(value)}
        // Empty clears the field rather than writing 0 — a blank dose and a dose
        // of zero mean different things on a prescription.
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    );
  }

  return (
    <input
      type="text"
      className={inputClass}
      value={(value as string) ?? ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

/**
 * Editable list of billable items.
 *
 * Rows stay collapsed to a name plus a one-line summary so a long prescription
 * is still scannable; expanding one reveals only the fields that section
 * actually has on the EMR.
 */
export const EditableItemList = <T extends NamedItem>({
  items,
  fields,
  onChange,
  renderMeta,
}: {
  items: T[];
  fields: ItemField[];
  onChange: (next: T[]) => void;
  renderMeta: (item: T) => string;
}) => {
  const [expanded, setExpanded] = useState<number | null>(null);

  const updateField = (index: number, field: ItemField, value: unknown) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  return (
    <ul className="space-y-1.5">
      {items.map((item, index) => {
        const isOpen = expanded === index;
        const meta = renderMeta(item);
        // "Every X weeks" is the only frequency that needs the X, so hide it otherwise.
        const visibleFields = fields.filter(
          (field) => field !== "x_value" || String(read(item, "frequency") ?? "").includes("Every X"),
        );

        return (
          <li
            key={index}
            className="rounded-lg border border-slate-200 bg-white transition-colors hover:border-indigo-200"
          >
            <div className="flex items-start gap-2 px-2.5 py-2">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : index)}
                className="flex min-w-0 flex-1 items-start gap-1.5 text-left"
              >
                <ChevronDown
                  className={clsx("mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform", isOpen && "rotate-180")}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold leading-snug text-slate-800">
                    {item.name || "(unnamed)"}
                  </span>
                  {meta && <span className="mt-0.5 block text-[11px] leading-snug text-slate-500">{meta}</span>}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  onChange(items.filter((_, i) => i !== index));
                  setExpanded(null);
                }}
                className="mt-0.5 shrink-0 rounded-md p-1 text-slate-300 transition-all hover:bg-red-50 hover:text-red-500"
                aria-label={`Remove ${item.name || "item"}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {isOpen && (
              <div className="grid grid-cols-2 gap-2.5 border-t border-slate-100 bg-slate-50/60 px-2.5 py-2.5">
                {visibleFields.map((field) => (
                  <label key={field} className={field === "remark" ? "col-span-2" : undefined}>
                    <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      {FIELD_LABEL[field]}
                    </span>
                    <ItemFieldInput
                      field={field}
                      value={read(item, field)}
                      onChange={(next) => updateField(index, field, next)}
                    />
                  </label>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * Catalog search for adding an item the doctor mentioned but the agent missed.
 *
 * Searches the same catalog the voice agent does, so an added item carries a real
 * id — an invented one cannot be billed.
 */
export const ItemSearchAdd = <T extends NamedItem>({
  searchType,
  clinicId,
  petId,
  emrId,
  emrType,
  placeholder,
  toItem,
  onAdd,
}: {
  searchType: SectionSearchDataTypes;
  clinicId: string;
  petId: string;
  emrId: string;
  emrType?: string;
  placeholder: string;
  toItem: (result: IEMRSectionSearchResponseData) => T;
  onAdd: (item: T) => void;
}) => {
  const { execute, searchResults, clearSearchResults } = useEMRSectionSearchAPI();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (query.trim().length < 2) {
      clearSearchResults();
      setSearching(false);
      return;
    }
    setSearching(true);
    // Debounced: the doctor is typing a drug name, not issuing one query per key.
    timerRef.current = setTimeout(() => {
      void execute(query.trim(), searchType, clinicId, petId, emrId, emrType).finally(() => setSearching(false));
    }, 350);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, searchType, clinicId, petId, emrId, emrType]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 rounded-lg border border-indigo-100 bg-indigo-50 px-2.5 py-1.5 text-[11px] font-semibold text-indigo-700 transition-colors hover:border-indigo-200 hover:bg-indigo-100"
      >
        <Plus className="h-3 w-3" />
        Add
      </button>
    );
  }

  return (
    <div className="mt-2">
      <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 transition-colors focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/20">
        <Search className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <input
          autoFocus
          type="text"
          value={query}
          placeholder={placeholder}
          onChange={(e) => setQuery(e.target.value)}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[11px] text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-0"
        />
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setQuery("");
            clearSearchResults();
          }}
          className="shrink-0 text-slate-400 transition-colors hover:text-slate-600"
          aria-label="Close search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {query.trim().length >= 2 && (
        <ul className="mt-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          {searching && <li className="px-2.5 py-2 text-[11px] text-slate-400">Searching…</li>}
          {!searching && searchResults.length === 0 && (
            <li className="px-2.5 py-2 text-[11px] text-slate-400">No matches in the catalog</li>
          )}
          {!searching &&
            searchResults.map((result, i) => (
              <li key={`${result.item_id ?? result.vetic_variant_id ?? i}`}>
                <button
                  type="button"
                  onClick={() => {
                    onAdd(toItem(result));
                    setOpen(false);
                    setQuery("");
                    clearSearchResults();
                  }}
                  className="w-full px-2.5 py-2 text-left text-[11px] text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-800"
                >
                  {result.variant_name || result.item_name || result.name}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};
